import { LoadingOutlined } from '@ant-design/icons'
import { AutoComplete, AutoCompleteProps, Button, Divider, Input, Select, SelectProps, Typography } from 'antd'
import { uniq } from 'lodash'
import React, { useCallback, useMemo, useState } from 'react'
import { useSearchNormalizedSuppliers } from '../../api'
import { useDebounce } from '../../routes/components/useDebounce'

export const NormalizedManufacturerSearch: React.FC<AutoCompleteProps> = ({ placeholder, ...props }) => {
  const [searchText, setSearchText] = useState(props.value)
  const [limit, setLimit] = useState(100)
  const debounceSearchtext = useDebounce(searchText, 800)
  const { data, loading } = useSearchNormalizedSuppliers({
    variables: { searchText: debounceSearchtext, limit },
    skip: !searchText,
  })

  const options = useMemo(() => {
    return (
      [
        ...(searchText ? [searchText] : []),
        ...(data?.searchNormalizedSuppliers.data
          .filter(m => m.normalizedCurvo !== searchText)
          .map(m => m.normalizedCurvo) || []),
      ].map(m => ({
        label: m,
        value: m,
      })) || []
    )
  }, [data?.searchNormalizedSuppliers, searchText])

  const dropdownRender = useCallback(
    (menu: React.ReactNode) => {
      return (
        <>
          {menu}
          {loading && (
            <Typography.Text style={{ margin: '0px 0 0px 8px' }} type="secondary">
              Searching ...
            </Typography.Text>
          )}
          {data?.searchNormalizedSuppliers &&
            data?.searchNormalizedSuppliers.total > data?.searchNormalizedSuppliers.data.length && (
              <>
                <Divider style={{ margin: '8px 0' }} />
                <Button
                  type="link"
                  onClick={() => setLimit(data?.searchNormalizedSuppliers.total)}
                  style={{ whiteSpace: 'nowrap', padding: '0 8px 4px' }}>
                  Show all {data?.searchNormalizedSuppliers.total} results
                </Button>
              </>
            )}
        </>
      )
    },
    [data?.searchNormalizedSuppliers, loading],
  )

  return (
    <AutoComplete
      style={{
        width: '100%',
      }}
      allowClear
      clearIcon={loading ? <LoadingOutlined spin /> : undefined}
      options={options}
      dropdownRender={dropdownRender}
      onSearch={v => setSearchText(v)}
      dropdownMatchSelectWidth={false}
      {...props}>
      {/* a custom input takes the placeholder itself */}
      <Input size="middle" placeholder={typeof placeholder === 'string' ? placeholder : undefined} />
    </AutoComplete>
  )
}

export const MultipleNormalizedManufacturerSearch: React.FC<SelectProps> = ({ style, ...props }) => {
  const [searchText, setSearchText] = useState('')
  const [limit, setLimit] = useState(100)
  const debounceSearchtext = useDebounce(searchText, 800)
  const { data, loading } = useSearchNormalizedSuppliers({
    variables: { searchText: debounceSearchtext, limit },
    skip: !searchText,
  })

  const options = useMemo(() => {
    return uniq([
      ...(searchText ? [searchText] : []),
      ...(props.value ? [props.value].flat() : []),
      ...(data?.searchNormalizedSuppliers.data
        .filter(m => m.normalizedCurvo !== searchText)
        .map(m => m.normalizedCurvo) || []),
    ]).map(m => ({
      label: m,
      value: m,
    }))
  }, [data?.searchNormalizedSuppliers, props.value, searchText])

  const dropdownRender = useCallback(
    (menu: React.ReactNode) => {
      return (
        <>
          {menu}
          {loading && (
            <Typography.Text style={{ margin: '0px 0 0px 8px' }} type="secondary">
              Searching ...
            </Typography.Text>
          )}
          {data?.searchNormalizedSuppliers &&
            data?.searchNormalizedSuppliers.total > data?.searchNormalizedSuppliers.data.length && (
              <>
                <Divider style={{ margin: '8px 0' }} />
                <Button
                  type="link"
                  onClick={() => setLimit(data?.searchNormalizedSuppliers.total)}
                  style={{ whiteSpace: 'nowrap', padding: '0 8px 4px' }}>
                  Show all {data?.searchNormalizedSuppliers.total} results
                </Button>
              </>
            )}
        </>
      )
    },
    [data?.searchNormalizedSuppliers, loading],
  )

  return (
    <Select
      style={{ width: '100%', ...style }}
      allowClear
      clearIcon={loading ? <LoadingOutlined spin /> : undefined}
      options={options}
      dropdownRender={dropdownRender}
      onSearch={v => setSearchText(v)}
      dropdownMatchSelectWidth={false}
      notFoundContent={null}
      mode="multiple"
      placeholder="Start typing to search"
      {...props}
    />
  )
}
